import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { connect } from 'react-redux';
import { initialize, updateSyncErrors } from 'redux-form';

import { useSnackbar } from 'notistack';

import { Typography, Button } from '@material-ui/core';

import { AppHeader, CircularLoader, UserForm } from '../../components';
import EmployeesTable from './Table';
import ConfirmContext from '../../contexts/ConfirmContext';

import { USER_TYPE } from '../../constants/base';

import { EXISTING_BUSINESS_OWNERS } from '../../graphql/queries/business-owner';
import {
  CREATE_BUSINESS_OWNER,
  DELETE_BUSINESS_OWNER,
  UPDATE_BUSINESS_OWNER_USER
} from '../../graphql/mutations/business-owner';

import useStyles from './styles';

const Users = props => {
  const { initializeForm, businessOwner, updateFormErrors } = props;

  const classes = useStyles();
  const [t] = useTranslation();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [page, setPage] = React.useState(0);

  const [createUser] = useMutation(CREATE_BUSINESS_OWNER);
  const [deleteUser] = useMutation(DELETE_BUSINESS_OWNER);
  const [updateUser] = useMutation(UPDATE_BUSINESS_OWNER_USER);
  const { data, refetch } = useQuery(EXISTING_BUSINESS_OWNERS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      page: page
    }
  });

  const initializeForms = initialFormsData => {
    Object.keys(initialFormsData).forEach(field => {
      initializeForm(field, initialFormsData[field]);
    });
  };

  const openCreateUserDialog = () => {
    initializeForms({
      usersForm: {
        isNotify: {
          Notify: false
        }
      }
      
    });
    setIsUpdate(false);
    setOpen(true);
  };

  const openEditUser = user => {
    setIsUpdate(true);
    setOpen(true);
    initializeForms({
      usersForm: {
        ...user,
        isNotify: {
          Notify: user?.isNotify || false
        }
      }
    });
  };

  const deleteUserHandler = item => {
    confirm({
      title: `Delete user?`,
      description: `${item.firstName} ${item.lastName}`,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    }).then(() => {
      deleteUser({ variables: { businessOwnerId: item.id } }).then(() => {
        enqueueSnackbar(t('User was successfully deleted!'), {
          variant: 'success'
        });
        if (data.businessOwners.length <= 1 && page) {
          refetch({
            page: page - 1
          });
          setPage(page - 1);
        } else {
          refetch({ page });
        }
      });
    });
  };

  const onPageChangeHandler = page => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  const showUserError = error => {
    if (
      error &&
      error.message === 'GraphQL error: User with this email is already in use'
    ) {
      updateFormErrors({ email: 'This email is already in use' });
      return;
    }

    enqueueSnackbar(t('Something was wrong'), {
      variant: 'error'
    });
  };

  const createUserHandler = data => {
    createUser({ variables: { businessOwner: data } })
      .then(() => {
        if (page) {
          refetch({
            page: page - 1
          });
          setPage(page - 1);
        } else {
          refetch();
        }
        enqueueSnackbar(t('User was successfully created!'), {
          variant: 'success'
        });
        setOpen(false);
        setLoading(false);
        refetch();
      })
      .catch(error => {
        setLoading(false);
        showUserError(error);
      });
  };

  const updateUserHandler = data => {
    updateUser({ variables: { businessOwner: data } })
      .then(() => {
        refetch();
        enqueueSnackbar(t('User was successfully updated!'), {
          variant: 'success'
        });
        setOpen(false);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        showUserError(error);
      });
  };

  const onSubmitHandler = values => {
    const data = { ...values };
    data.isNotify = data.isNotify.Notify;
    setLoading(true);

    if (isUpdate) {
      delete data.__typename;
      delete data.referencePrefix;
      updateUserHandler(data);
    } else {
      const userData = {
        ...values,
        ownerId: businessOwner.selectedBusinessOwner.id,
        userType: USER_TYPE.employee,
        companyName: ''
      };
      createUserHandler(userData);
    }
  };

  return (
    <div className={classes.root}>
      <AppHeader />

      <CircularLoader
        isLoading={loading}
        className={classes.loader}
        loaderClassName={classes.loaderClassName}
      />

      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <Typography variant="h2" className={classes.toolbarTitle}>
            {t('Users')}
          </Typography>
        </div>
        <div className={classes.toolbarRight}>
          <Button
            variant="outlined"
            color="primary"
            classes={{ root: classes.buttonRoot }}
            onClick={e => {
              e.stopPropagation();
              openCreateUserDialog();
            }}
          >
            {t('Add user')}
          </Button>
        </div>
      </div>

      <EmployeesTable
        data={data}
        openEditUser={openEditUser}
        deleteUserHandler={deleteUserHandler}
        page={page}
        onPageChangeHandler={onPageChangeHandler}
      />

      {open && (
        <UserForm
          isUpdate={isUpdate}
          onClose={() => setOpen(false)}
          open={true}
          onSubmit={onSubmitHandler}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  usersForm: state.form.usersForm,
  businessOwner: state.businessOwner
});

const mapDispatchToProps = dispatch => ({
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  updateFormErrors: fields => {
    dispatch(updateSyncErrors('usersForm', fields));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Users);
